import type * as THREE from 'three';
import type { Vec3 } from '../api/types.js';
import type {
  InitOptions,
  LoadOptions,
  ViewerCapabilities,
  ViewerEventDetailMap,
} from './types.js';

export interface CameraFit {
  readonly pos: Vec3;
  readonly lookAt: Vec3;
  readonly fov: number;
}

export interface CapturedFrame {
  readonly blob: Blob;
  revoke(): void;
}

export interface ViewerAdapter {
  readonly capabilities: ViewerCapabilities;
  readonly events: EventTarget;

  init(opts: InitOptions): Promise<void>;
  loadSplatScene(url: string, opts?: LoadOptions): Promise<void>;
  getThreeScene(): THREE.Scene;
  getCamera(): THREE.PerspectiveCamera;
  getRenderer(): THREE.WebGLRenderer;
  setCamera(
    pos: readonly [number, number, number],
    lookAt: readonly [number, number, number],
    fov?: number,
  ): void;

  readonly raycast?: (screenX: number, screenY: number) => THREE.Vector3 | null;
  readonly enterXR?: (mode: 'vr' | 'ar') => Promise<void>;
  readonly exitXR?: () => Promise<void>;
  readonly dispose?: () => Promise<void>;
  readonly removeSplatScene?: (sceneIndex: number) => Promise<void>;
  // Per-frame hook called before viewer.update()/render(). Used by 4.C
  // OrbitControls (controls.update() each frame). Pass `null` to clear.
  readonly setBeforeRender?: (cb: (() => void) | null) => void;

  // 4.E: subscription to per-frame ticks (for FPS monitor). Returns unsubscribe.
  readonly onFrame?: (cb: (now: number) => void) => () => void;

  // 4.E: schedules a one-shot capture after the next render. Resolves with PNG
  // blob. Implementation MUST capture synchronously inside the same RAF tick
  // following viewer.render() to avoid the browser clearing the drawing buffer
  // (no preserveDrawingBuffer always-on, see SPIKE_4C0_REPORT.md + STEP4E).
  readonly captureFrame?: () => Promise<CapturedFrame>;

  // 4.E: compute auto-fit camera from current splat mesh bbox. Returns null
  // if the mesh is unavailable or has zero extent (caller falls back to default).
  readonly computeFitCamera?: () => CameraFit | null;
}

export type ViewerAdapterEvent<K extends keyof ViewerEventDetailMap> = CustomEvent<
  ViewerEventDetailMap[K]
>;

type RaycastFn = NonNullable<ViewerAdapter['raycast']>;
type EnterXRFn = NonNullable<ViewerAdapter['enterXR']>;
type ExitXRFn = NonNullable<ViewerAdapter['exitXR']>;
type DisposeFn = NonNullable<ViewerAdapter['dispose']>;
type RemoveSplatSceneFn = NonNullable<ViewerAdapter['removeSplatScene']>;
type SetBeforeRenderFn = NonNullable<ViewerAdapter['setBeforeRender']>;
type OnFrameFn = NonNullable<ViewerAdapter['onFrame']>;
type CaptureFrameFn = NonNullable<ViewerAdapter['captureFrame']>;
type ComputeFitCameraFn = NonNullable<ViewerAdapter['computeFitCamera']>;

export type WithRaycast = ViewerAdapter & {
  readonly capabilities: ViewerCapabilities & { readonly raycast: true };
  readonly raycast: RaycastFn;
};

export type WithEnterXR = ViewerAdapter & {
  readonly capabilities: ViewerCapabilities & { readonly enterXR: true };
  readonly enterXR: EnterXRFn;
};

export type WithExitXR = ViewerAdapter & {
  readonly capabilities: ViewerCapabilities & { readonly exitXR: true };
  readonly exitXR: ExitXRFn;
};

export type WithDispose = ViewerAdapter & {
  readonly capabilities: ViewerCapabilities & { readonly dispose: true };
  readonly dispose: DisposeFn;
};

export type WithRemoveSplatScene = ViewerAdapter & {
  readonly capabilities: ViewerCapabilities & { readonly removeSplatScene: true };
  readonly removeSplatScene: RemoveSplatSceneFn;
};

export type WithBeforeRender = ViewerAdapter & {
  readonly capabilities: ViewerCapabilities & { readonly beforeRenderHook: true };
  readonly setBeforeRender: SetBeforeRenderFn;
};

export type WithFrameCallbacks = ViewerAdapter & {
  readonly capabilities: ViewerCapabilities & { readonly frameCallbacks: true };
  readonly onFrame: OnFrameFn;
};

export type WithCaptureFrame = ViewerAdapter & {
  readonly capabilities: ViewerCapabilities & { readonly captureFrame: true };
  readonly captureFrame: CaptureFrameFn;
};

export type WithCameraFit = ViewerAdapter & {
  readonly capabilities: ViewerCapabilities & { readonly cameraFit: true };
  readonly computeFitCamera: ComputeFitCameraFn;
};

export function canRaycast(adapter: ViewerAdapter): adapter is WithRaycast {
  return adapter.capabilities.raycast === true && typeof adapter.raycast === 'function';
}

export function canEnterXR(adapter: ViewerAdapter): adapter is WithEnterXR {
  return adapter.capabilities.enterXR === true && typeof adapter.enterXR === 'function';
}

export function canExitXR(adapter: ViewerAdapter): adapter is WithExitXR {
  return adapter.capabilities.exitXR === true && typeof adapter.exitXR === 'function';
}

export function canDispose(adapter: ViewerAdapter): adapter is WithDispose {
  return adapter.capabilities.dispose === true && typeof adapter.dispose === 'function';
}

export function canRemoveSplatScene(adapter: ViewerAdapter): adapter is WithRemoveSplatScene {
  return (
    adapter.capabilities.removeSplatScene === true && typeof adapter.removeSplatScene === 'function'
  );
}

export function canSetBeforeRender(adapter: ViewerAdapter): adapter is WithBeforeRender {
  return (
    adapter.capabilities.beforeRenderHook === true && typeof adapter.setBeforeRender === 'function'
  );
}

export function canSubscribeFrames(adapter: ViewerAdapter): adapter is WithFrameCallbacks {
  return adapter.capabilities.frameCallbacks === true && typeof adapter.onFrame === 'function';
}

export function canCaptureFrame(adapter: ViewerAdapter): adapter is WithCaptureFrame {
  return adapter.capabilities.captureFrame === true && typeof adapter.captureFrame === 'function';
}

export function canFitCamera(adapter: ViewerAdapter): adapter is WithCameraFit {
  return adapter.capabilities.cameraFit === true && typeof adapter.computeFitCamera === 'function';
}
